<template>
  <Dialog
    v-model:visible="visible"
    modal
    header="ליד חדש"
    :style="{ width: 'min(560px, 95vw)' }"
    :draggable="false"
  >
    <div class="flex flex-col gap-4 text-sm">
      <div class="bg-blue-50 border border-blue-100 rounded-lg px-3 py-2 text-xs text-blue-800 leading-relaxed">
        ליד חדש ניתן להוסיף גם בלי שהעסק נרשם עדיין. אם נרשמו כבר, אפשר לקשר את ה-BID בשלב מאוחר יותר.
      </div>

      <div class="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div class="flex flex-col gap-1.5">
          <label class="text-gray-600 font-medium">שם העסק <span class="text-red-500">*</span></label>
          <InputText v-model="form.businessName" placeholder="חיוני ביוטי" class="!text-base" />
        </div>
        <div class="flex flex-col gap-1.5">
          <label class="text-gray-600 font-medium">איש קשר</label>
          <InputText v-model="form.contactName" placeholder="שם פרטי + משפחה" />
        </div>
        <div class="flex flex-col gap-1.5">
          <label class="text-gray-600 font-medium">טלפון</label>
          <InputText v-model="form.phone" placeholder="050-1234567" dir="ltr" />
        </div>
        <div class="flex flex-col gap-1.5">
          <label class="text-gray-600 font-medium">אימייל</label>
          <InputText v-model="form.email" placeholder="email@example.com" dir="ltr" />
        </div>
        <div class="flex flex-col gap-1.5">
          <label class="text-gray-600 font-medium">מקור</label>
          <Select v-model="form.source" :options="sourceOptions" optionLabel="label" optionValue="value" class="w-full" />
        </div>
        <div class="flex flex-col gap-1.5">
          <label class="text-gray-600 font-medium">עדיפות</label>
          <Select v-model="form.priority" :options="priorityOptions" optionLabel="label" optionValue="value" class="w-full" />
        </div>
        <div class="flex flex-col gap-1.5">
          <label class="text-gray-600 font-medium">שלב</label>
          <Select v-model="form.stage" :options="stageOptions" optionLabel="label" optionValue="value" class="w-full" />
        </div>
        <div class="flex flex-col gap-1.5">
          <label class="text-gray-600 font-medium">ערך צפוי (₪)</label>
          <InputNumber v-model="form.expectedValue" :min="0" :step="50" placeholder="לדוגמה 199" suffix=" ₪" />
        </div>
        <div class="flex flex-col gap-1.5 md:col-span-2">
          <label class="text-gray-600 font-medium">BID (אופציונלי - אם העסק כבר נרשם)</label>
          <InputText v-model="form.bid" placeholder="הוסף BID אם העסק רשום במערכת" />
        </div>
        <div class="flex flex-col gap-1.5 md:col-span-2">
          <label class="text-gray-600 font-medium">הערה ראשונית</label>
          <Textarea v-model="form.initialNote" rows="2" autoResize placeholder="הקשר, פרטים, מאיפה הגיע הליד..." />
        </div>
      </div>

      <div v-if="error" class="bg-red-50 border border-red-200 rounded-lg px-4 py-2.5 text-red-600 text-xs">
        {{ error }}
      </div>
    </div>
    <template #footer>
      <div class="flex justify-end items-center gap-2 w-full">
        <Button label="ביטול" text size="small" @click="cancel" />
        <Button
          icon="pi pi-check"
          label="צור ליד"
          size="small"
          :loading="saving"
          class="!bg-gradient-to-r !from-purple-600 !to-indigo-600 !text-white !border-0"
          @click="submit"
        />
      </div>
    </template>
  </Dialog>
</template>

<script setup lang="ts">
import { reactive, ref, watch } from 'vue';
import {
  IPlatformLead,
  PlatformLeadActivityTypeEnum,
  PlatformLeadPriorityEnum,
  PlatformLeadSourceEnum,
  PlatformLeadStageEnum,
} from '@torim-app/types';
import { CrmApi } from '../api/crm-api';
import { PRIORITY_LABELS, SOURCE_LABELS, STAGE_LABELS } from '../composables/useLeadDisplay';

const props = defineProps<{
  modelValue: boolean;
  defaultBid?: string;
  defaultBusinessName?: string;
}>();

const emit = defineEmits<{
  (e: 'update:modelValue', value: boolean): void;
  (e: 'created', lead: IPlatformLead): void;
}>();

const visible = ref(props.modelValue);
const saving = ref(false);
const error = ref('');

watch(() => props.modelValue, (v) => { visible.value = v; if (v) reset(); });
watch(visible, (v) => emit('update:modelValue', v));

interface FormState {
  businessName: string;
  contactName: string;
  phone: string;
  email: string;
  bid: string;
  source: PlatformLeadSourceEnum;
  priority: PlatformLeadPriorityEnum;
  stage: PlatformLeadStageEnum;
  expectedValue: number | null;
  initialNote: string;
}

const form = reactive<FormState>({
  businessName: '',
  contactName: '',
  phone: '',
  email: '',
  bid: '',
  source: PlatformLeadSourceEnum.Manual,
  priority: PlatformLeadPriorityEnum.Medium,
  stage: PlatformLeadStageEnum.New,
  expectedValue: null,
  initialNote: '',
});

const reset = () => {
  form.businessName = props.defaultBusinessName || '';
  form.contactName = '';
  form.phone = '';
  form.email = '';
  form.bid = props.defaultBid || '';
  form.source = props.defaultBid ? PlatformLeadSourceEnum.Signup : PlatformLeadSourceEnum.Manual;
  form.priority = PlatformLeadPriorityEnum.Medium;
  form.stage = PlatformLeadStageEnum.New;
  form.expectedValue = null;
  form.initialNote = '';
  error.value = '';
};

const sourceOptions = Object.values(PlatformLeadSourceEnum).map((value) => ({
  value,
  label: SOURCE_LABELS[value],
}));

const priorityOptions = Object.values(PlatformLeadPriorityEnum).map((value) => ({
  value,
  label: PRIORITY_LABELS[value],
}));

const stageOptions = Object.values(PlatformLeadStageEnum).map((value) => ({
  value,
  label: STAGE_LABELS[value],
}));

const cancel = () => {
  visible.value = false;
};

const submit = async () => {
  if (!form.businessName.trim()) {
    error.value = 'שם העסק חובה';
    return;
  }
  saving.value = true;
  error.value = '';
  try {
    const initialNotes = form.initialNote.trim()
      ? [{
          text: form.initialNote.trim(),
          type: PlatformLeadActivityTypeEnum.Note,
          createdAt: new Date().toISOString(),
        }]
      : [];

    const created = await CrmApi.createLead({
      businessName: form.businessName.trim(),
      contactName: form.contactName.trim() || undefined,
      phone: form.phone.trim(),
      email: form.email.trim(),
      bid: form.bid.trim() || undefined,
      source: form.source,
      priority: form.priority,
      stage: form.stage,
      expectedValue: form.expectedValue ?? undefined,
      notes: initialNotes,
      requestedFeatures: [],
    });
    emit('created', created);
    visible.value = false;
  } catch (e) {
    console.error('Failed to create lead:', e);
    error.value = typeof e === 'string' ? e : 'יצירת הליד נכשלה';
  } finally {
    saving.value = false;
  }
};
</script>
